import { FC, useEffect, createContext, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isDispatched } from "@ctra/utils";

import { Enterprise, EnterpriseAppState, User, UserEntity } from "@ctra/api";

interface ContextType {
  user: Partial<UserEntity>;
  meta: { isLoading: boolean; isAdmin: boolean };
}

/**
 * Make a default context for user entities
 */
const DefaultContext = createContext<ContextType>({
  user: {},
  meta: { isLoading: true, isAdmin: false }
});

/**
 * Fetch the current user object if it is not yet present in the store
 * @param children
 * @private
 */
const _UserProvider: FC = ({ children }) => {
  const dispatch = useDispatch();

  /**
   * Get the currently logged-in username from the auth state
   */
  const username = useSelector<EnterpriseAppState, UserEntity["username"]>(Enterprise.entities.getUsername);

  /**
   * Attempt to get the user object
   * @type {UserEntity | undefined}
   */
  const user = useSelector<EnterpriseAppState, UserEntity | undefined>((state) =>
    Enterprise.entities.getUser(state, { username })
  );

  /**
   * Check if the user is an admin
   * @type {boolean}
   */
  const isAdmin = useSelector<EnterpriseAppState, boolean>(Enterprise.entities.isAdmin);

  /**
   * Tell whether the fetching action has been dispatched
   */
  const dispatched = useSelector<EnterpriseAppState, boolean>((state) =>
    isDispatched(state, User.types.FETCH_USER)
  );

  useEffect(() => {
    if (!(dispatched || user)) {
      dispatch(User.actions.fetchUser.start(username as string));
    }
  }, [dispatch, dispatched, user, username]);

  return (
    <DefaultContext.Provider
      value={{
        user: user || {
          username
        },
        meta: { isLoading: !user, isAdmin }
      }}
    >
      {children}
    </DefaultContext.Provider>
  );
};

export const UserContext = {
  Consumer: DefaultContext.Consumer,
  Provider: _UserProvider
};

/**
 * Hook to get the user info within the context
 */
export const useCurrentUser = (): ContextType => useContext(DefaultContext);
